import { memo, useCallback, useEffect, useRef } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import Group from 'components/utils/Group'
import IconButton from 'LuxKit/Button/IconButton'
import LineTimesIcon from 'LuxKit/Icons/line/LineTimesIcon'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import TextButton from 'LuxKit/Button/TextButton'
import StickyPrompt from 'components/Common/StickyPromptCard/StickyPrompt'
import zIndex from 'styles/tools/z-index'
import useClickOutside from 'hooks/useClickOutside'
import useLeLivePrompt from 'leLive/hooks/useLeLivePrompt'
import { useAppSelector } from 'hooks/reduxHooks'
import { trackEvent } from 'analytics/analytics'
import { clientEvent } from 'analytics/snowplow/events'
import { useCurrencyFormatter } from 'hooks/useNumberFormatters'

const LE_LIVE_UTM = 'utm_source=luxuryescapes&utm_medium=web&utm_campaign=le_live_banner_2026&utm_content=popup'

const PromptWrapper = styled.div`
  position: relative;
  z-index: ${zIndex.promoDisplayConfig};
`

const StyledPrompt = styled(StickyPrompt)`
  padding: ${rem(40)} ${rem(24)} ${rem(24)};
  width: ${rem(360)};
  max-width: calc(100vw - ${rem(32)});
`

const HeadingWrapper = styled.div`
  padding: 0 ${rem(20)};
  width: 100%;
`

const CloseButton = styled(IconButton)`
  position: absolute;
  top: ${rem(12)};
  inset-inline-end: ${rem(12)};
  z-index: 1;
`

function LeLivePrompt() {
  const intl = useIntl()
  const formatAudCurrency = useCurrencyFormatter({ currency: 'AUD' })
  const promptRef = useRef<HTMLDivElement>(null)
  const { show, dismiss } = useLeLivePrompt()
  const currentRegionCode = useAppSelector(state => state.geo.currentRegionCode)
  const leLiveUrl = `/${currentRegionCode?.toLowerCase() || 'au'}/live?${LE_LIVE_UTM}`

  const handleDismiss = useCallback(() => {
    trackEvent(clientEvent({
      subject: 'le_live_popup',
      action: 'dismiss',
      category: 'le_live',
      type: 'interaction',
    }))
    dismiss()
  }, [dismiss])

  useClickOutside(promptRef, handleDismiss, show)

  useEffect(() => {
    if (show) {
      trackEvent(clientEvent({
        subject: 'le_live_popup',
        action: 'view',
        category: 'le_live',
        type: 'nonInteraction',
      }))
    }
  }, [show])

  const handleBannerClick = () => {
    trackEvent(clientEvent({
      subject: 'le_live_popup',
      action: 'click',
      category: 'le_live',
      type: 'interaction',
    }))
    dismiss()
  }

  const headingText = intl.formatMessage({
    id: 'gmReju8Rrah28Zy39VrP',
    defaultMessage: "Don't Miss Luxury\u00A0Escapes\u00A0Live",
    description: 'Heading for the LE Live event notification popup displayed on the homepage',
  })

  const bodyText = intl.formatMessage(
    {
      id: 'tL5efw80xsjtjkJ_QEzt',
      defaultMessage: 'Check out our first-ever travel expo at the Royal Exhibition Building on Saturday 16 and Sunday 17 May 2026. Featuring over {giveawayValue} in giveaways, exclusive offers and celebrity guests.',
      description: 'Body text for the LE Live event notification popup describing the event details and highlights',
    },
    { giveawayValue: formatAudCurrency(100000, 'roundedDollar') },
  )

  const ctaText = intl.formatMessage({
    id: 'B173wjYadrx-gBIncyBz',
    defaultMessage: 'Secure your free ticket',
    description: 'Call-to-action button text in the LE Live event popup directing users to free tickets',
  })

  const closeLabelText = intl.formatMessage({
    id: 'Z_EoBUQWX1DSkLC-GVCl',
    defaultMessage: 'Close LE Live event popup',
    description: 'Accessible label for the close button on the LE Live event notification popup',
  })

  return (
    <PromptWrapper role="dialog">
      <StyledPrompt show={show} sticky={false} ref={promptRef}>
        <CloseButton
          kind="tertiary"
          shape="square"
          onClick={handleDismiss}
          aria-label={closeLabelText}
        >
          <LineTimesIcon />
        </CloseButton>
        <Group direction="vertical" gap={16} fullWidth>
          <HeadingWrapper>
            <Heading variant="heading3" align="center">{headingText}</Heading>
          </HeadingWrapper>
          <BodyText variant="medium" align="center">{bodyText}</BodyText>
          <TextButton kind="primary" fit="full-width" to={leLiveUrl} onClick={handleBannerClick}>
            {ctaText}
          </TextButton>
        </Group>
      </StyledPrompt>
    </PromptWrapper>
  )
}

export default memo(LeLivePrompt)
